import { createPortal } from "react-dom";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { type ITooltip, Tooltip as ReactTooltip } from "react-tooltip";
import { cn } from "../lib/utils";

/** Above `Modal` (`z-[200000]`) so hover chrome still shows in fullscreen submission dialogs. */
export const PORTALED_TOOLTIP_Z_INDEX = 200010;

// Portals to document.body so the tooltip escapes ancestor overflow:hidden / CSS transform containing blocks.
export const PortaledTooltip = (props: ITooltip) => {
	return createPortal(
		<ReactTooltip
			{...props}
			positionStrategy="fixed"
			style={{ zIndex: PORTALED_TOOLTIP_Z_INDEX, ...props.style }}
		/>,
		document.body,
	);
};

type TooltipProps = {
	id: string;
	content: string;
	className?: string;
	focusable?: boolean;
	group?: string; // optional: for tooltip grouping
	ariaLabel?: string;
	clickable?: boolean; // optional: to make the tooltip clickable
	condensed?: boolean;
	maxWidth?: number | string; // optional: constrain the tooltip box so long content wraps
};

export const Tooltip = ({
	id,
	content,
	className,
	focusable = false,
	group,
	ariaLabel,
	clickable = false,
	condensed = false,
	maxWidth,
}: TooltipProps) => {
	return (
		<>
			<HiOutlineInformationCircle
				id={`${id}`}
				tabIndex={focusable ? 0 : -1}
				role="button"
				aria-label={ariaLabel || "Tooltip information"}
				data-tooltip-html={content}
				data-tooltip-id={id}
				data-tooltip-group={group}
				className={cn(
					"text-[22px] inline-block cursor-pointer focus:outline-none text-gray-400 fill-gray-50 hover:text-secondary stroke-[1.75px] shrink-0",
					className,
					condensed && "text-[16px]",
				)}
			/>
			{/* The tooltip box renders inline, so `font-normal` keeps it from inheriting a bold anchor. */}
			<ReactTooltip
				id={id}
				clickable={clickable}
				className="font-normal"
				style={{ zIndex: 9999, maxWidth }}
			/>
		</>
	);
};
